import cn from "classnames";
import { type FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import type { NotificationDto } from "shared";
import { Icon } from "../common/Icon";
import { LinkGen } from "../LinkGen";
import {
	useNotificationActions,
	useNotifications,
	useNotificationUnreadCount,
} from "./useNotifications";
import "./NotificationBell.css";

const formatWhen = (iso: string) => {
	const date = new Date(iso);
	const now = new Date();
	const sameDay =
		date.getFullYear() === now.getFullYear() &&
		date.getMonth() === now.getMonth() &&
		date.getDate() === now.getDate();

	if (sameDay) {
		return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
	}

	return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const resolveActionUrl = (notification: NotificationDto): string => {
	if (notification.actionUrl) {
		return notification.actionUrl;
	}
	if (notification.postId) {
		return LinkGen.Post(notification.postId);
	}
	return LinkGen.Calendar();
};

export const NotificationBell: FC = () => {
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const { data: unread } = useNotificationUnreadCount();
	const { data: notifications = [], isLoading } = useNotifications(open);
	const { markRead, markAllRead } = useNotificationActions();

	const unreadCount = unread?.count ?? 0;

	useEffect(() => {
		if (!open) {
			return;
		}

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setOpen(false);
			}
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.body.style.overflow = previousOverflow;
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open]);

	const handleOpen = () => {
		setOpen((current) => !current);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleNotificationClick = async (notification: NotificationDto) => {
		if (!notification.readAt) {
			await markRead.mutateAsync(notification.id);
		}
		setOpen(false);
		navigate(resolveActionUrl(notification));
	};

	const handleMarkAllRead = async () => {
		if (unreadCount === 0) {
			return;
		}
		await markAllRead.mutateAsync();
	};

	return (
		<>
			<div className="notification-bell">
				<button
					type="button"
					className="notification-bell-trigger"
					aria-label={
						unreadCount > 0
							? `${unreadCount} unread notifications`
							: "Notifications"
					}
					aria-expanded={open}
					onClick={handleOpen}
				>
					<Icon name="notifications" size={22} />
					{unreadCount > 0 && (
						<span className="notification-bell-badge">
							{unreadCount > 9 ? "9+" : unreadCount}
						</span>
					)}
				</button>
			</div>

			{open &&
				createPortal(
					<>
						<button
							type="button"
							className="notification-bell-backdrop"
							aria-label="Close notifications"
							onClick={handleClose}
						/>
						<aside
							className="notification-bell-drawer"
							role="dialog"
							aria-label="Notifications"
						>
							<div className="notification-bell-drawer-header">
								<strong>Notifications</strong>
								<div className="notification-bell-drawer-actions">
									{unreadCount > 0 && (
										<button
											type="button"
											className="notification-bell-mark-all"
											onClick={handleMarkAllRead}
											disabled={markAllRead.isPending}
										>
											Mark all read
										</button>
									)}
									<button
										type="button"
										className="notification-bell-close"
										aria-label="Close"
										onClick={handleClose}
									>
										<Icon name="close" size={20} />
									</button>
								</div>
							</div>

							<div className="notification-bell-list">
								{isLoading && (
									<p className="notification-bell-empty">Loading…</p>
								)}
								{!isLoading && notifications.length === 0 && (
									<p className="notification-bell-empty">
										No notifications yet
									</p>
								)}
								{notifications.map((notification) => (
									<button
										key={notification.id}
										type="button"
										className={cn("notification-bell-item", {
											unread: !notification.readAt,
										})}
										onClick={() => handleNotificationClick(notification)}
									>
										<span className="notification-bell-item-title">
											{notification.title}
										</span>
										<span className="notification-bell-item-message">
											{notification.message}
										</span>
										<span className="notification-bell-item-time">
											{formatWhen(notification.createdAt)}
										</span>
									</button>
								))}
							</div>
						</aside>
					</>,
					document.body,
				)}
		</>
	);
};
